/**
 * BannerCropper — upload + crop UI for the card bottom banner.
 *
 * Flow:
 *   1. User clicks "Upload banner" → native file picker
 *   2. Selected image loads into a modal cropper
 *   3. Fixed 5:1 aspect crop box, user drags/resizes within the source
 *   4. "Save crop" → canvas crops client-side → uploaded as JPEG
 *   5. "Cancel" → modal closes, nothing uploaded
 *
 * Why this matters:
 *   - Banner slot on the card is ~5:1 ratio; letting tenants upload
 *     any aspect ratio and cover-fitting in jsPDF produces weird
 *     crops (the "beard photo as banner" edge case).
 *   - Cropping client-side means R2 stores the final 5:1 JPEG, and
 *     jsPDF just embeds it cleanly — no overlay-masking hacks.
 *
 * Uses `react-image-crop` (MIT, ~40KB gzipped). Randy must run
 * `npm install react-image-crop` before deploying.
 *
 * Replaces the `PhotoUpload` component usage on the bottom-banner
 * slot in IdCardsPage.jsx. All other PhotoUpload usage (logo, staff
 * photos) remains unchanged.
 */

import { useCallback, useRef, useState } from 'react';
import ReactCrop, { centerCrop, makeAspectCrop } from 'react-image-crop';
import 'react-image-crop/dist/ReactCrop.css';

const BANNER_ASPECT = 5; // 5:1 — width divided by height
const OUTPUT_WIDTH = 1500; // Upload a 1500×300 JPEG regardless of source size
const OUTPUT_HEIGHT = OUTPUT_WIDTH / BANNER_ASPECT;
const JPEG_QUALITY = 0.9;
const MAX_SOURCE_MB = 10; // Pre-crop source may be larger than the 2MB post-crop limit

/**
 * @param props.currentUrl      existing banner image URL (null if none)
 * @param props.onUpload        async (file: Blob) => any
 * @param props.onDelete        async () => any
 * @param props.disabled        boolean (e.g. disabled when template=minimal)
 */
export function BannerCropper({ currentUrl, onUpload, onDelete, disabled = false }) {
  const fileInputRef = useRef(null);

  const [sourceUrl, setSourceUrl] = useState(null);       // data URL of the picked file
  const [crop, setCrop] = useState(undefined);            // active crop selection
  const [completedCrop, setCompletedCrop] = useState(undefined);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState(null);

  const imgRef = useRef(null);

  /* ─────────────────────────────────────────────────── */
  /*  Pick file                                          */
  /* ─────────────────────────────────────────────────── */

  const handlePickClick = () => {
    if (disabled) return;
    fileInputRef.current?.click();
  };

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    // Reset the input so the same file can be re-picked after cancel
    if (e.target) e.target.value = '';

    if (!file) return;

    if (!/^image\/(jpeg|jpg|png|webp)$/i.test(file.type)) {
      setError('Please pick a JPEG, PNG, or WebP image.');
      return;
    }

    if (file.size > MAX_SOURCE_MB * 1024 * 1024) {
      setError(`Source image is too large. Max ${MAX_SOURCE_MB}MB before cropping.`);
      return;
    }

    setError(null);

    // Read as data URL for the cropper to display
    const reader = new FileReader();
    reader.onload = () => setSourceUrl(reader.result);
    reader.onerror = () => setError('Could not read that file. Try another.');
    reader.readAsDataURL(file);
  };

  /* ─────────────────────────────────────────────────── */
  /*  Initialise crop when image loads                   */
  /* ─────────────────────────────────────────────────── */

  const handleImageLoad = (e) => {
    const { naturalWidth: w, naturalHeight: h } = e.currentTarget;
    // Start with as wide a 5:1 crop as fits, centred vertically
    const initialCrop = centerCrop(
      makeAspectCrop(
        { unit: '%', width: 90 },
        BANNER_ASPECT,
        w,
        h,
      ),
      w,
      h,
    );
    setCrop(initialCrop);
  };

  /* ─────────────────────────────────────────────────── */
  /*  Save (canvas crop + upload)                        */
  /* ─────────────────────────────────────────────────── */

  const handleSave = useCallback(async () => {
    if (!imgRef.current || !completedCrop) return;

    setUploading(true);
    setError(null);

    try {
      const blob = await cropToBlob(imgRef.current, completedCrop);
      if (!blob) throw new Error('Canvas crop failed');

      // Wrap blob in a File so the backend's formData.get('bottom_image')
      // sees a filename (some servers/Workers check for one).
      const cropped = new File([blob], 'banner.jpg', { type: 'image/jpeg' });

      await onUpload(cropped);

      // Success — close modal
      setSourceUrl(null);
      setCrop(undefined);
      setCompletedCrop(undefined);
    } catch (err) {
      console.error('[BannerCropper] upload failed:', err);
      setError(err?.message || 'Upload failed. Try again.');
    } finally {
      setUploading(false);
    }
  }, [completedCrop, onUpload]);

  const handleCancel = () => {
    setSourceUrl(null);
    setCrop(undefined);
    setCompletedCrop(undefined);
    setError(null);
  };

  const handleDelete = async () => {
    if (!onDelete) return;
    if (!confirm('Remove banner image? Cards will fall back to the default mountain range.')) return;
    try {
      await onDelete();
    } catch (err) {
      console.error('[BannerCropper] delete failed:', err);
      setError(err?.message || 'Delete failed.');
    }
  };

  /* ─────────────────────────────────────────────────── */
  /*  Render                                             */
  /* ─────────────────────────────────────────────────── */

  return (
    <div className={`banner-cropper ${disabled ? 'is-disabled' : ''}`}>

      {/* Current state */}
      <div className="bc-current">
        {currentUrl ? (
          <div className="bc-thumb">
            <img src={currentUrl} alt="Current banner" />
          </div>
        ) : (
          <div className="bc-thumb bc-thumb--empty" aria-label="No banner uploaded">
            <span>No banner</span>
          </div>
        )}

        <div className="bc-actions">
          <button
            type="button"
            className="btn btn-ghost"
            onClick={handlePickClick}
            disabled={disabled}
          >
            {currentUrl ? 'Replace banner' : 'Upload banner'}
          </button>
          {currentUrl && onDelete && (
            <button
              type="button"
              className="btn btn-link bc-remove"
              onClick={handleDelete}
              disabled={disabled}
            >
              Remove
            </button>
          )}
        </div>
      </div>

      <p className="field-hint">
        5:1 ratio (e.g. 1500×300). Cropped to fit before upload — you'll
        be able to adjust the crop.
      </p>

      {error && (
        <div className="alert alert-error" role="alert" style={{ marginTop: 'var(--sp-3)' }}>
          {error}
        </div>
      )}

      <input
        ref={fileInputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp"
        style={{ display: 'none' }}
        onChange={handleFileChange}
      />

      {/* Cropper modal */}
      {sourceUrl && (
        <div className="bc-modal" role="dialog" aria-modal="true" aria-labelledby="bc-modal-title">
          <div className="bc-modal-backdrop" onClick={handleCancel} />
          <div className="bc-modal-card">
            <header className="bc-modal-header">
              <h2 id="bc-modal-title">Crop banner</h2>
              <p className="muted" style={{ fontSize: 'var(--fs-xs)' }}>
                Drag to reposition. Resize the corners. Ratio is locked to 5:1
                to match the card banner slot.
              </p>
            </header>

            <div className="bc-modal-body">
              <ReactCrop
                crop={crop}
                onChange={(_, percentCrop) => setCrop(percentCrop)}
                onComplete={(c) => setCompletedCrop(c)}
                aspect={BANNER_ASPECT}
                minWidth={100}
                keepSelection
              >
                <img
                  ref={imgRef}
                  src={sourceUrl}
                  alt="Crop source"
                  onLoad={handleImageLoad}
                  style={{ maxWidth: '100%', maxHeight: '60vh' }}
                />
              </ReactCrop>
            </div>

            <footer className="bc-modal-footer">
              <button
                type="button"
                className="btn btn-ghost"
                onClick={handleCancel}
                disabled={uploading}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn btn-primary"
                onClick={handleSave}
                disabled={uploading || !completedCrop}
                aria-busy={uploading}
              >
                {uploading
                  ? (<><span className="spinner" aria-hidden="true" /> Uploading…</>)
                  : 'Save crop'}
              </button>
            </footer>
          </div>
        </div>
      )}
    </div>
  );
}

/* ─────────────────────────────────────────────────── */
/*  Helper: canvas-based crop → JPEG blob              */
/* ─────────────────────────────────────────────────── */

/**
 * Crop the source image according to the completed crop rect and
 * return a JPEG Blob at a standard 1500×300 output size.
 *
 * `completedCrop` units match the `<ReactCrop unit>` — we use 'px'
 * by default when the completion fires, but the crop state above
 * uses '%' for display. React-image-crop gives us pixel values in
 * the onComplete callback regardless, scaled to the rendered image
 * size on screen. We normalise via the natural/displayed size ratio.
 */
async function cropToBlob(imgEl, completedCrop) {
  const scaleX = imgEl.naturalWidth / imgEl.width;
  const scaleY = imgEl.naturalHeight / imgEl.height;

  // completedCrop comes in with .unit — handle both 'px' and '%'
  let sx, sy, sw, sh;
  if (completedCrop.unit === '%') {
    sx = (completedCrop.x / 100) * imgEl.naturalWidth;
    sy = (completedCrop.y / 100) * imgEl.naturalHeight;
    sw = (completedCrop.width / 100) * imgEl.naturalWidth;
    sh = (completedCrop.height / 100) * imgEl.naturalHeight;
  } else {
    sx = completedCrop.x * scaleX;
    sy = completedCrop.y * scaleY;
    sw = completedCrop.width * scaleX;
    sh = completedCrop.height * scaleY;
  }

  const canvas = document.createElement('canvas');
  canvas.width = OUTPUT_WIDTH;
  canvas.height = OUTPUT_HEIGHT;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('Canvas 2D context unavailable');

  // High-quality resize
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = 'high';

  // Draw the cropped region scaled to our standard output size
  ctx.drawImage(imgEl, sx, sy, sw, sh, 0, 0, OUTPUT_WIDTH, OUTPUT_HEIGHT);

  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (blob) resolve(blob);
        else reject(new Error('canvas.toBlob returned null'));
      },
      'image/jpeg',
      JPEG_QUALITY,
    );
  });
}
